'use client';

import React, { useEffect } from 'react';
import {
  CodeSetBlock,
  StandardBlock,
  ProfileSelectorDropdown,
  SitesBySiteOfCareType,
} from './components';
import {
  faCircleDollar,
  faBriefcaseMedical,
  faHospital,
  faScalpel,
  faBedPulse,
} from '@fortawesome/pro-regular-svg-icons';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useLogging } from 'src/context/logging';
import { LOCATION } from 'src/hooks/filters/constants';
import useProfileSelections from 'src/hooks/profiles/useProfileSelections';
import { NewPayerMix } from 'src/modules/Profile/components/Views/components/NewPayerMix';
import ProfileSystemDetails from './components/ProfileDetails/ProfileSystemDetails';
import { EnrichingCodesInfo } from '../PulseUI';
import { exists } from 'src/utils/exists/exists';
import { formatCurrency } from 'src/utils/formatCurrency/formatCurrency';
import { useFlags } from 'launchdarkly-react-client-sdk';

const SystemProfile = () => {
  const { dataCoverage: dataCoverageFlag } = useFlags();
  const { entityDetails } = useProfile();
  const log = useLogging();
  const { selectedFilterSet, selectedCohort } = useProfileSelections(
    entityDetails?.type
  );

  const dataCoverage = selectedFilterSet?.detailed_filters?.flatMap(
    (filter) =>
      filter.detailed_values?.map((value: any) => value.vrdc_enriched) ?? []
  );
  // Log page view
  useEffect(() => {
    log.event('System profile viewed', {
      source: 'SystemProfile',
      systemId: entityDetails?.id || null,
      systemName: entityDetails?.name || null,
      totalSites: entityDetails?.total_sites || 0,
    });
  }, [entityDetails, log]);

  return (
    <div className="h-full w-full max-w-7xl mx-auto overflow-hidden pt-4">
      <div className="h-full space-y-4">
        <div className="flex items-center justify-between">
          <div className="inline-flex flex-col justify-end items-start gap-2">
            <div className="text-primary text-base font-bold font-['Inter'] leading-normal">
              Filter by Code Set
            </div>
            <ProfileSelectorDropdown location={LOCATION.SYSTEM} />
          </div>
          {(selectedFilterSet || selectedCohort) && dataCoverageFlag && (
            <EnrichingCodesInfo
              dataCoverage={dataCoverage}
              isEncounter={exists(selectedCohort)}
            />
          )}
        </div>
        {/* Detail blocks */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Code Set Block */}
          <CodeSetBlock />
          {/* Standard Blocks */}
          <StandardBlock
            icon={faHospital}
            title="Sites"
            detail={(entityDetails?.total_sites || 0).toLocaleString()}
          />
          <StandardBlock
            icon={faScalpel}
            title="Operating Rooms"
            detail={(entityDetails?.operating_room_count || 0).toLocaleString()}
          />
          <StandardBlock
            icon={faBedPulse}
            title="Beds"
            detail={(entityDetails?.total_bed_count || 0).toLocaleString()}
          />
          <StandardBlock
            icon={faCircleDollar}
            title="Total Patient Revenue"
            detail={formatCurrency(entityDetails?.total_patient_revenue || 0)}
          />
          <StandardBlock
            icon={faBriefcaseMedical}
            title="Current Assets"
            detail={formatCurrency(entityDetails?.total_current_assets || 0)}
          />
          <StandardBlock
            icon={faBriefcaseMedical}
            title="Total Assets"
            detail={formatCurrency(entityDetails?.total_assets || 0)}
          />
          <StandardBlock
            icon={faBriefcaseMedical}
            title="Total Liability"
            detail={formatCurrency(
              entityDetails?.total_current_liabilities || 0
            )}
          />
        </div>

        {/* Payer Mix (left, wider) and Details + Sites breakdown (right) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 min-h-0">
            <NewPayerMix showLegend />
          </div>
          <div className="flex flex-col gap-4 lg:col-span-1 min-h-0">
            <ProfileSystemDetails />
            <SitesBySiteOfCareType />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemProfile;
